import type {
  CameraPose,
  DebugSettings,
  ENU,
  GeoFix,
  LatLon,
  LocalFrame,
  OrientationProvider,
  OrientationSample,
  PoseState,
  PositionProvider,
  RenderMode,
  Settings,
  Store,
  WorldAlignment,
  XrViewerSample,
} from '../contracts';
import { debugStore, settingsStore } from '../settings';
import { HeadingHistory, computeAlignment, thetaXrDeg, xrPitchDeg } from './alignment';
import { QuatSmoother, composeCameraQuat, sensorToWorld, viewportVFovDeg } from './camera';
import { CircularLowPass, type CircularLowPassOptions, LowPass } from './circular';
import { TangentPlaneFrame } from './frame';
import { GpsGateTracker, type GpsGateOptions } from './gate';
import { HudRotationTracker } from './hud';
import { KalmanCV2D, type KalmanOptions } from './kalman';
import {
  DEG2RAD,
  type HeadingPitch,
  type Quat,
  earthHeadingPitch,
  metresPerDegree,
  quatCopy,
  wrap180,
  wrap360,
  yUpHeadingPitch,
} from './math';
import { SpeedEstimator } from './speed';

export interface PoseEngineOptions {
  settings?: Store<Settings>;
  debug?: Store<DebugSettings>;
  mode?: RenderMode;
  /** Current screen.orientation.angle (camera/sim). Default: read from `screen.orientation`. */
  getScreenAngleDeg?: () => number;
  /**
   * Portrait (long-side) camera FOV fed into `viewportVFovDeg` for modes 'camera'/'sim'
   * (deg). Default: the debug store's `cameraVFovDeg` (sim debug slider, unchanged
   * behaviour); camera mode overrides it with the fixed device FOV constant.
   */
  getVFovBaseDeg?: () => number;
  kalman?: Partial<KalmanOptions>;
  gate?: Partial<GpsGateOptions>;
  /** Options of the adaptive yaw filter (XR alignment). */
  yawFilter?: Partial<CircularLowPassOptions>;
}

/** Numbers for the debug overlay (mutated in place by update()). */
export interface PoseDiagnostics {
  /** Last raw alignment measurement alpha = thetaTrue - thetaXr (deg), NaN if none. */
  yawMeasDeg: number;
  /** Filtered alpha before trim (deg, true-north based: alpha_mag + declination), NaN if none. */
  yawFilteredDeg: number;
  yawFilterFast: boolean;
  /** Number of accepted yaw measurements. */
  yawSamples: number;
  /** 1-sigma Kalman position uncertainty (m), NaN before the first fix. */
  kalmanSigmaM: number;
  speedSource: 'doppler' | 'kalman' | null;
  orientationSource: OrientationSample['source'] | null;
  /** Age of the latest orientation sample (ms), NaN if none. */
  orientationAgeMs: number;
  /** Total number of orientation samples received (this engine instance). */
  orientationSampleCount: number;
  /** Latest raw heading relative to MAGNETIC north (deg), NaN if none. */
  rawHeadingMagDeg: number;
  /** Latest raw camera-forward pitch (deg, + = up), NaN if none. */
  rawPitchDeg: number;
  /** Time since the last non-null XR viewer pose (ms), NaN if none. */
  xrPoseAgeMs: number;
  /** Declination used (deg). */
  declinationDeg: number;
  /** Meridian convergence of the active frame at the user (deg). */
  frameCorrectionDeg: number;
  /** Physical device roll relative to the locked XR screen (deg), NaN if unknown. */
  hudRawDeg: number;
}

/** Tunables of the engine (exported for tests / the debug overlay). */
export const POSE_CONSTANTS = {
  /** Slerp smoothing of the sim-mode camera (s). */
  slerpTauS: 0.08,
  /** XR viewer pose null for longer than this -> tracking 'lost' (ms). */
  xrLostMs: 1000,
  /** Orientation samples older than this are not used for the XR alignment (ms). */
  maxSampleAgeMs: 250,
  /** Alignment only while |camera pitch| is below this (deg). */
  maxAlignPitchDeg: 45,
  /** Alignment only while the XR heading turns slower than this (deg/s). */
  maxAlignRateDegS: 60,
  /** Water-plane low-pass time constant (s). */
  waterTauS: 10,
  /** Compass/sim: orientation older than this -> tracking 'limited' (ms). */
  compassStaleMs: 5000,
  /** Doppler velocity measurement sigma for the Kalman filter (m/s). */
  velocitySigma: 0.5,
} as const;

function defaultScreenAngle(): number {
  const s = (globalThis as { screen?: { orientation?: { angle?: number } } }).screen;
  const a = s?.orientation?.angle;
  if (typeof a === 'number') return a;
  // iOS < 16.4 (and some iPadOS Safari builds): no screen.orientation, only the deprecated
  // window.orientation (0/90/-90/180). Same sign as .angle (browsers define
  // window.orientation as the angle with 270 reported as -90), so only wrap into [0, 360).
  const w = (globalThis as { orientation?: number }).orientation;
  return typeof w === 'number' ? wrap360(w) : 0;
}

/**
 * Pose fusion: GPS Kalman position (all modes) + orientation sensor + WebXR.
 *
 * Feed fixes (addFix / attach) and orientation samples (addOrientation /
 * attach), set the lake frame (setFrame) and call update(now, xr) once per
 * rendered frame. update() creates no objects/arrays and returns the same
 * PoseState object every time (nested objects are reused too): read it,
 * don't keep references across frames.
 *
 * Without a lake frame, userENU is null and camera.position / alignment t
 * refer to an internal tangent frame anchored at the first fix (so heading,
 * speed, lat/lon and a horizon keep working). setFrame() re-projects the
 * filter state, so positions are continuous in the new frame.
 *
 */
export class PoseEngine {
  readonly state: PoseState;
  readonly diagnostics: PoseDiagnostics;

  private readonly settings: Store<Settings>;
  private readonly debug: Store<DebugSettings>;
  private readonly getScreenAngleDeg: () => number;
  private readonly getVFovBaseDeg: () => number;
  private mode: RenderMode;

  // Frames
  private frame: LocalFrame | null = null;
  private internalFrame: TangentPlaneFrame | null = null;
  /** headingCorrectionDeg of the active frame at the last fix. */
  private corrDeg = 0;

  // Position
  private readonly kalman: KalmanCV2D;
  private readonly gate: GpsGateTracker;
  private readonly speed = new SpeedEstimator();
  private lastFix: GeoFix | null = null;
  private readonly fixLatLon: LatLon = { lat: 0, lon: 0 };
  private readonly pred: ENU = { e: 0, n: 0 };
  private readonly userENU: ENU = { e: 0, n: 0 };
  private readonly userLatLon: LatLon = { lat: 0, lon: 0 };
  // Linearisation for per-frame lat/lon (set at each fix).
  private anchorE = 0;
  private anchorN = 0;
  private anchorLat = 0;
  private anchorLon = 0;
  private readonly mPerDeg = { lat: 111000, lon: 75000 };

  // Orientation sensor
  private hasOri = false;
  private readonly oriQ: Quat = [0, 0, 0, 1];
  private oriTime = 0;
  private oriSource: OrientationSample['source'] | null = null;
  private oriSampleCount = 0;
  private oriHeadingMagDeg = NaN;
  private oriPitchDeg = NaN;
  /** Set by the app when every orientation provider has failed to start (B.4/B.6). */
  private orientationFailed = false;
  private readonly hp: HeadingPitch = { headingDeg: 0, pitchDeg: 0 };

  // XR
  private readonly history = new HeadingHistory(32);
  private readonly yawFilter: CircularLowPass;
  private readonly camY = new LowPass(POSE_CONSTANTS.waterTauS);
  private readonly hud = new HudRotationTracker();
  private lastXrPoseTime = -Infinity;
  private xrModeStart = NaN;
  private xrWasLost = false;
  private lastYawSampleTime = -Infinity;
  private readonly alignment: WorldAlignment = { yawRad: 0, tx: 0, waterY: 0, tz: 0 };
  private alignmentValid = false;

  // Compass / sim
  private readonly smoother = new QuatSmoother(POSE_CONSTANTS.slerpTauS);
  private readonly qWorld: Quat = [0, 0, 0, 1];
  private readonly camera: CameraPose = { position: [0, 0, 0], quaternion: [0, 0, 0, 1], vFovDeg: 63 };
  private aspect = 0.5;

  private autoDeclinationDeg = 3.6;
  private lastNow = NaN;

  constructor(opts: PoseEngineOptions = {}) {
    this.settings = opts.settings ?? settingsStore;
    this.debug = opts.debug ?? debugStore;
    this.getScreenAngleDeg = opts.getScreenAngleDeg ?? defaultScreenAngle;
    this.getVFovBaseDeg = opts.getVFovBaseDeg ?? (() => this.debug.get().cameraVFovDeg);
    this.mode = opts.mode ?? 'sim';
    this.kalman = new KalmanCV2D(opts.kalman);
    this.gate = new GpsGateTracker(opts.gate);
    this.yawFilter = new CircularLowPass(opts.yawFilter);
    this.state = {
      mode: this.mode,
      userENU: null,
      userLatLon: null,
      gpsAccuracy: null,
      gpsGate: 'waiting',
      speedKmh: null,
      courseDeg: null,
      headingDeg: null,
      pitchDeg: null,
      tracking: 'lost',
      alignment: null,
      camera: null,
    };
    this.diagnostics = {
      yawMeasDeg: NaN,
      yawFilteredDeg: NaN,
      yawFilterFast: false,
      yawSamples: 0,
      kalmanSigmaM: NaN,
      speedSource: null,
      orientationSource: null,
      orientationAgeMs: NaN,
      xrPoseAgeMs: NaN,
      declinationDeg: 3.6,
      frameCorrectionDeg: 0,
      hudRawDeg: NaN,
      orientationSampleCount: 0,
      rawHeadingMagDeg: NaN,
      rawPitchDeg: NaN,
    };
  }

  // ─── Configuration ────────────────────────────────────────────────────────

  getMode(): RenderMode {
    return this.mode;
  }

  /** Switch the render mode. Entering 'xr' starts a fresh alignment (new session). */
  setMode(mode: RenderMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    if (mode === 'xr') this.resetAlignment();
    else this.smoother.reset();
    this.hud.reset();
  }

  /** Declination (deg, + = east) used when settings.declinationMode is 'auto' (from the geo module). */
  setAutoDeclinationDeg(deg: number): void {
    if (Number.isFinite(deg)) this.autoDeclinationDeg = deg;
  }

  /** Viewport size in CSS px (compass/sim FOV in landscape). */
  setViewport(width: number, height: number): void {
    if (width > 0 && height > 0) this.aspect = width / height;
  }

  /** Active lake frame (null = none). The filter state is re-projected into the new frame. */
  setFrame(frame: LocalFrame | null): void {
    if (frame === this.frame) return;
    const old = this.activeFrame();
    this.frame = frame;
    if (!frame) {
      this.internalFrame = null;
      if (this.kalman.initialized && old) {
        const ll = old.toLatLon({ e: this.kalman.e, n: this.kalman.n });
        this.internalFrame = new TangentPlaneFrame(ll);
      }
    }
    const neu = this.activeFrame();
    if (old && neu) this.reproject(old, neu);
  }

  getFrame(): LocalFrame | null {
    return this.frame;
  }

  /** Forget the XR yaw/height alignment (new session). */
  resetAlignment(): void {
    this.yawFilter.reset();
    this.camY.reset();
    this.history.clear();
    this.alignmentValid = false;
    this.lastXrPoseTime = -Infinity;
    this.xrModeStart = NaN;
    this.xrWasLost = false;
    this.lastYawSampleTime = -Infinity;
    this.diagnostics.yawSamples = 0;
  }

  /**
   * The XR reference space was reset (XRReferenceSpace 'reset' event): the
   * XR frame may have jumped, so re-converge quickly.
   */
  notifyXrReset(): void {
    this.yawFilter.boost(2);
    this.history.clear();
    this.camY.reset();
  }

  /** HUD counter-rotation (deg, 0/90/180/270). 0 outside XR (the page rotates natively). */
  get hudRotationDeg(): number {
    return this.mode === 'xr' ? this.hud.deg : 0;
  }

  // ─── Inputs ───────────────────────────────────────────────────────────────

  addFix(fix: GeoFix): void {
    if (!Number.isFinite(fix.lat) || !Number.isFinite(fix.lon) || !Number.isFinite(fix.time)) return;
    const acc = Number.isFinite(fix.accuracy) && fix.accuracy > 0 ? fix.accuracy : 50;
    this.lastFix = fix;
    this.gate.addFix(acc, fix.time);
    this.speed.addFix(fix);

    const ll = this.fixLatLon;
    ll.lat = fix.lat;
    ll.lon = fix.lon;
    if (!this.frame && !this.internalFrame) this.internalFrame = new TangentPlaneFrame(ll);
    let frame = this.activeFrame() as LocalFrame;

    let p = frame.toENU(ll);
    if (!this.frame && this.internalFrame && p.e * p.e + p.n * p.n > 2000 * 2000) {
      // Re-anchor the stand-in frame so its flat-earth error stays small.
      const next = new TangentPlaneFrame(ll);
      this.reproject(frame, next);
      this.internalFrame = next;
      frame = next;
      p = frame.toENU(ll);
    }
    this.corrDeg = frame.headingCorrectionDeg(ll);

    const accepted = this.kalman.updatePosition(p.e, p.n, acc, fix.time);
    if (accepted && fix.speed !== null && Number.isFinite(fix.speed)) {
      const sp = fix.speed;
      if (fix.course !== null && Number.isFinite(fix.course) && sp >= 0.5) {
        const c = (fix.course + this.corrDeg) * DEG2RAD;
        this.kalman.updateVelocity(sp * Math.sin(c), sp * Math.cos(c), POSE_CONSTANTS.velocitySigma);
      } else if (sp < 0.2) {
        this.kalman.updateVelocity(0, 0, POSE_CONSTANTS.velocitySigma);
      }
    }
    this.updateAnchor(frame);
  }

  addOrientation(s: OrientationSample): void {
    const q = s.quaternion;
    if (!Number.isFinite(q[0] + q[1] + q[2] + q[3])) return;
    quatCopy(q, this.oriQ);
    this.oriTime = s.time;
    this.oriSource = s.source;
    this.oriHeadingMagDeg = s.headingMagDeg;
    this.oriPitchDeg = s.pitchDeg;
    this.oriSampleCount++;
    this.hasOri = true;
  }

  /**
   * Tell the engine whether every orientation provider has failed (no compass at all).
   * While true and no magnetic yaw sample has ever converged, the XR alignment falls
   * back to declination/frame correction only, so the overlay still appears (B.4).
   */
  setOrientationFailed(failed: boolean): void {
    this.orientationFailed = failed;
  }

  /** Subscribe to providers; returns an unsubscribe function. */
  attach(position?: PositionProvider | null, orientation?: OrientationProvider | null): () => void {
    const offs: (() => void)[] = [];
    if (position) {
      const f = position.latest();
      if (f) this.addFix(f);
      offs.push(position.onFix((fix) => this.addFix(fix)));
    }
    if (orientation) {
      const s = orientation.latest();
      if (s) this.addOrientation(s);
      offs.push(orientation.onSample((sample) => this.addOrientation(sample)));
    }
    return () => offs.forEach((off) => off());
  }

  // ─── Per frame ────────────────────────────────────────────────────────────

  update(now: number, xr?: XrViewerSample | null): PoseState {
    let dtS = Number.isNaN(this.lastNow) ? 0 : (now - this.lastNow) / 1000;
    if (dtS < 0) dtS = 0;
    else if (dtS > 0.5) dtS = 0.5;
    this.lastNow = now;

    const set = this.settings.get();
    const st = this.state;
    const dg = this.diagnostics;
    st.mode = this.mode;

    // Position (all modes)
    const k = this.kalman;
    if (k.initialized) {
      k.predictedPosition(now, this.pred);
      if (this.frame) {
        this.userENU.e = this.pred.e;
        this.userENU.n = this.pred.n;
        st.userENU = this.userENU;
      } else {
        st.userENU = null;
      }
      this.computeLatLon();
      st.userLatLon = this.userLatLon;
      dg.kalmanSigmaM = k.positionSigma();
    } else {
      st.userENU = null;
      st.userLatLon = null;
    }
    st.gpsAccuracy = this.lastFix ? this.lastFix.accuracy : null;
    st.gpsGate = this.gate.update(now, set.gpsThresholdM);
    this.speed.update(now, k.initialized, k.ve, k.vn, this.corrDeg);
    st.speedKmh = this.speed.speedKmh;
    st.courseDeg = this.speed.courseDeg;
    dg.speedSource = this.speed.source;

    const decl = set.declinationMode === 'auto' ? this.autoDeclinationDeg : set.declinationDeg;
    dg.declinationDeg = decl;
    dg.frameCorrectionDeg = this.corrDeg;
    dg.orientationSource = this.oriSource;
    dg.orientationAgeMs = this.hasOri ? now - this.oriTime : NaN;
    dg.orientationSampleCount = this.oriSampleCount;
    dg.rawHeadingMagDeg = this.hasOri ? this.oriHeadingMagDeg : NaN;
    dg.rawPitchDeg = this.hasOri ? this.oriPitchDeg : NaN;

    if (this.mode === 'xr') this.updateXr(now, dtS, xr ?? null, decl, set);
    else this.updateCamera(now, dtS, decl, set);
    return st;
  }

  // ─── Internals ────────────────────────────────────────────────────────────

  private activeFrame(): LocalFrame | null {
    return this.frame ?? this.internalFrame;
  }

  private reproject(old: LocalFrame, neu: LocalFrame): void {
    const k = this.kalman;
    if (!k.initialized) return;
    const ll = old.toLatLon({ e: k.e, n: k.n });
    const p = neu.toENU(ll);
    const corrNew = neu.headingCorrectionDeg(ll);
    const r = (corrNew - old.headingCorrectionDeg(ll)) * DEG2RAD;
    const c = Math.cos(r);
    const s = Math.sin(r);
    const ve = k.ve;
    const vn = k.vn;
    k.e = p.e;
    k.n = p.n;
    k.ve = ve * c + vn * s;
    k.vn = vn * c - ve * s;
    this.corrDeg = corrNew;
    this.updateAnchor(neu);
  }

  private updateAnchor(frame: LocalFrame): void {
    const k = this.kalman;
    if (!k.initialized) return;
    const ll = frame.toLatLon({ e: k.e, n: k.n });
    this.anchorE = k.e;
    this.anchorN = k.n;
    this.anchorLat = ll.lat;
    this.anchorLon = ll.lon;
    metresPerDegree(ll.lat, this.mPerDeg);
  }

  /** Lat/lon of this.pred via a local linearisation around the last fix (no allocation). */
  private computeLatLon(): void {
    const de = this.pred.e - this.anchorE;
    const dn = this.pred.n - this.anchorN;
    // frame axes -> true east/north (frame heading = true heading + corr)
    const r = this.corrDeg * DEG2RAD;
    const c = Math.cos(r);
    const s = Math.sin(r);
    const dE = de * c - dn * s;
    const dN = dn * c + de * s;
    this.userLatLon.lat = this.anchorLat + dN / this.mPerDeg.lat;
    this.userLatLon.lon = this.anchorLon + dE / this.mPerDeg.lon;
  }

  private sensorHeadingTrue(decl: number, upAngleDeg: number): number {
    earthHeadingPitch(this.oriQ, upAngleDeg, this.hp);
    return wrap360(this.hp.headingDeg + decl);
  }

  private updateXr(now: number, dtS: number, xr: XrViewerSample | null, decl: number, set: Settings): void {
    const st = this.state;
    const dg = this.diagnostics;
    st.camera = null;
    if (Number.isNaN(this.xrModeStart)) this.xrModeStart = now;

    if (!xr) {
      const since = now - Math.max(this.lastXrPoseTime, this.xrModeStart);
      const lost = since > POSE_CONSTANTS.xrLostMs;
      // Position comes from GPS, not XR: a brief pose gap (e.g. a single dropped frame) is
      // not a problem, so we only report 'limited' once the gap has lasted >= 1000 ms.
      st.tracking = lost ? 'lost' : since >= POSE_CONSTANTS.xrLostMs ? 'limited' : 'ok';
      if (lost) this.xrWasLost = true;
      dg.xrPoseAgeMs = Number.isFinite(this.lastXrPoseTime) ? now - this.lastXrPoseTime : NaN;
      st.alignment = this.alignmentValid ? this.alignment : null;
      // HUD numbers keep running from the orientation sensor.
      if (this.hasOri) {
        st.headingDeg = this.sensorHeadingTrue(decl, this.getScreenAngleDeg() + this.hud.deg);
        st.pitchDeg = this.hp.pitchDeg;
      }
      return;
    }

    if (this.xrWasLost) {
      // ARCore may relocalise with a different yaw/height after a loss.
      this.notifyXrReset();
      this.xrWasLost = false;
    }
    this.lastXrPoseTime = now;
    dg.xrPoseAgeMs = 0;
    // We only use XR for orientation (position comes from GPS), so an emulated position
    // (no 6DoF tracking) does not degrade tracking quality for our purposes.
    st.tracking = 'ok';
    const q = xr.orientation;
    this.hud.update(q);
    dg.hudRawDeg = this.hud.rawDeg;
    const theta = thetaXrDeg(q);
    const pitchXr = xrPitchDeg(q);
    this.history.push(xr.time, theta);
    this.camY.update(xr.position[1], dtS);

    // Yaw measurement from the newest orientation sample (each sample used once).
    if (this.hasOri && this.oriTime > this.lastYawSampleTime) {
      const tS = this.oriTime;
      const fresh = now - tS < POSE_CONSTANTS.maxSampleAgeMs;
      earthHeadingPitch(this.oriQ, 0, this.hp);
      if (
        fresh &&
        Math.abs(pitchXr) < POSE_CONSTANTS.maxAlignPitchDeg &&
        Math.abs(this.hp.pitchDeg) < POSE_CONSTANTS.maxAlignPitchDeg &&
        this.history.rateAt(tS) < POSE_CONSTANTS.maxAlignRateDegS
      ) {
        const thXr = this.history.at(tS);
        if (Number.isFinite(thXr)) {
          let dt = Number.isFinite(this.lastYawSampleTime) ? (tS - this.lastYawSampleTime) / 1000 : 0;
          if (dt > 0.1) dt = 0.1;
          // Filtered in magnetic terms (alpha_mag = thetaMag - thetaXr) so that a
          // declination change applies immediately; alpha = alpha_mag + decl.
          const meas = this.hp.headingDeg - thXr;
          this.yawFilter.update(meas, dt);
          dg.yawMeasDeg = wrap180(meas + decl);
          dg.yawSamples++;
          this.lastYawSampleTime = tS;
        }
      }
    }
    dg.yawFilteredDeg = this.yawFilter.initialized ? wrap180(this.yawFilter.value + decl) : NaN;
    dg.yawFilterFast = this.yawFilter.isFast();

    // Alignment. yaw = alpha_mag + declination + frame correction.
    if (this.yawFilter.initialized && this.kalman.initialized) {
      const yawDeg = wrap180(this.yawFilter.value + decl + this.corrDeg);
      computeAlignment(
        yawDeg * DEG2RAD,
        xr.position,
        this.pred.e,
        this.pred.n,
        this.camY.value - set.eyeHeightM,
        this.alignment,
      );
      this.alignmentValid = true;
      st.alignment = this.alignment;
    } else if (this.orientationFailed && this.kalman.initialized) {
      // No compass at all (B.4): align using declination/frame correction only, so the
      // overlay still appears even without any yaw source.
      const yawDeg = wrap180(decl + this.corrDeg);
      computeAlignment(
        yawDeg * DEG2RAD,
        xr.position,
        this.pred.e,
        this.pred.n,
        this.camY.value - set.eyeHeightM,
        this.alignment,
      );
      this.alignmentValid = true;
      st.alignment = this.alignment;
    } else {
      st.alignment = this.alignmentValid ? this.alignment : null;
    }

    // Heading/pitch for the HUD: XR orientation (smooth) + filtered alpha.
    yUpHeadingPitch(q, this.hud.deg, this.hp);
    st.pitchDeg = pitchXr;
    if (this.yawFilter.initialized) {
      st.headingDeg = wrap360(this.hp.headingDeg + this.yawFilter.value + decl);
    } else if (this.hasOri) {
      st.headingDeg = this.sensorHeadingTrue(decl, this.getScreenAngleDeg() + this.hud.deg);
    } else {
      st.headingDeg = null;
    }
  }

  private updateCamera(now: number, dtS: number, decl: number, set: Settings): void {
    const st = this.state;
    st.alignment = null;
    this.diagnostics.xrPoseAgeMs = NaN;
    if (!this.hasOri) {
      st.camera = null;
      st.tracking = 'lost';
      st.headingDeg = null;
      st.pitchDeg = null;
      return;
    }
    // deviceorientation only fires on change: a still device must not count as lost.
    st.tracking = now - this.oriTime < POSE_CONSTANTS.compassStaleMs ? 'ok' : 'limited';

    sensorToWorld(this.oriQ, this.qWorld);
    const qs = this.smoother.update(this.qWorld, dtS);
    const cam = this.camera;
    composeCameraQuat(qs, -decl - this.corrDeg, this.getScreenAngleDeg(), cam.quaternion);
    yUpHeadingPitch(cam.quaternion, 0, this.hp);
    st.headingDeg = wrap360(this.hp.headingDeg - this.corrDeg);
    st.pitchDeg = this.hp.pitchDeg;

    const h = set.eyeHeightM;
    if (this.kalman.initialized) {
      cam.position[0] = this.pred.e;
      cam.position[1] = h;
      cam.position[2] = -this.pred.n;
    } else {
      cam.position[0] = 0;
      cam.position[1] = h;
      cam.position[2] = 0;
    }
    cam.vFovDeg = viewportVFovDeg(this.getVFovBaseDeg(), this.aspect);
    st.camera = cam;
  }
}
